import type { AlgorithmChallenge } from '@code-arcade/engine'

export const insertionSort: AlgorithmChallenge = {
  id: 'insertion-sort',
  title: 'Insertion Sort',
  level: 1,
  chapter: 'chapter-1',
  mode: 'race-it',
  description: 'Pick up each element and slide it into position. O(n²) worst case — but O(n) when the data is nearly sorted.',

  realWorld: {
    scenario: "HR adds 3–5 new employee records to the admin table daily. The table is already sorted. Dev Lama re-sorts the whole thing every night.",
    consequence: "Re-sorting 500 already-sorted records with Bubble Sort wastes 124,750 comparisons. Insertion Sort detects the order and exits in ~503.",
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 25,
      character: 'stacy-queue',
      mood: 'happy',
      message: "The new employee records sorted instantly this morning! What did you change?",
    },
  ],

  complexity: { time: 'O(n²)', space: 'O(1)' },
  xpReward: { oneStar: 100, twoStar: 200, threeStar: 400 },

  keyTakeaway:
    'Real data is rarely random. Logs arrive nearly sorted by time. Prices cluster. User IDs are sequential. ' +
    'New records append to already-sorted lists. Insertion sort is O(n) on nearly-sorted data because it asks ' +
    '"is this element already in the right place?" before doing work. That question — stopping to characterize ' +
    'your input before choosing an approach — eliminates entire classes of unnecessary computation. TimSort uses ' +
    'insertion sort internally not because it is special, but because it knows something about the data at that ' +
    'point: after merge passes, small runs are almost always nearly sorted. Knowing your input\'s shape is as ' +
    'important as knowing the algorithm\'s complexity.',

  whenToUse: [
    'Nearly-sorted data — a few elements out of place (O(n + k) where k is inversions)',
    'Streaming data that arrives in nearly-sorted order — event logs, sensor readings, financial ticks',
    'Tiny arrays (< 16 elements) where constant factors dominate big-O notation',
    'Inside TimSort and introsort — you now know why the standard library reaches for insertion sort on small or nearly-sorted runs',
  ],

  whenNotToUse: [
    'Large randomly-shuffled arrays — O(n²) comparisons will kill you',
    'When data arrives in bulk and you can sort all at once — use Array.sort()',
    'Reverse-sorted input is the absolute worst case: every element shifts all the way left',
  ],

  pseudocode: `procedure insertionSort(A)
  n ← length(A)
  for i from 1 to n - 1
    key ← A[i]
    j ← i - 1
    while j ≥ 0 and A[j] > key
      A[j + 1] ← A[j]
      j ← j - 1
    A[j + 1] ← key
  return A`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void InsertionSort(int[] arr) {
    int n = arr.Length;
    for (int i = 1; i < n; i++) {
        int key = arr[i];
        int j = i - 1;
        while (j >= 0 && arr[j] > key) {
            arr[j + 1] = arr[j];
            j--;
        }
        arr[j + 1] = key;
    }
}

// arr.sort() uses THIS algorithm internally for small subarrays — you just saw why it works:
Array.Sort(arr);               // introspective sort — uses insertion sort internally for small runs
list.Sort();                   // List<T> — same internals`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def insertion_sort(arr: list[int]) -> list[int]:
    for i in range(1, len(arr)):
        key = arr[i]
        j = i - 1
        while j >= 0 and arr[j] > key:
            arr[j + 1] = arr[j]
            j -= 1
        arr[j + 1] = key
    return arr


# arr.sort() uses THIS algorithm internally for small subarrays — you just saw why it works:
arr.sort()                # TimSort — uses insertion sort for small runs
sorted_arr = sorted(arr)  # same algorithm, returns new list`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function insertionSort(arr) {
  for (let i = 1; i < arr.length; i++) {
    const key = arr[i];
    let j = i - 1;
    while (j >= 0 && arr[j] > key) {
      arr[j + 1] = arr[j];
      j--;
    }
    arr[j + 1] = key;
  }
  return arr;
}

// arr.sort() uses THIS algorithm internally for small subarrays — you just saw why it works:
arr.sort((a, b) => a - b); // V8 TimSort — uses insertion sort for small subarrays`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function insertionSort(arr: number[]): number[] {
  for (let i = 1; i < arr.length; i++) {
    const key = arr[i];
    let j = i - 1;
    while (j >= 0 && arr[j] > key) {
      arr[j + 1] = arr[j];
      j--;
    }
    arr[j + 1] = key;
  }
  return arr;
}

// arr.sort() uses THIS algorithm internally for small subarrays — you just saw why it works:
arr.sort((a, b) => a - b); // V8 TimSort — uses insertion sort for small subarrays`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void insertionSort(int[] arr) {
    int n = arr.length;
    for (int i = 1; i < n; i++) {
        int key = arr[i];
        int j = i - 1;
        while (j >= 0 && arr[j] > key) {
            arr[j + 1] = arr[j];
            j--;
        }
        arr[j + 1] = key;
    }
}

// arr.sort() uses THIS algorithm internally for small subarrays — you just saw why it works:
Arrays.sort(arr);                // dual-pivot quicksort + insertion sort for small runs
Arrays.sort(objArr, comparator); // TimSort for objects`,
    },
  ],

  sampleDataset: {
    label: 'Employee IDs — Innotek admin table (3 new hires added today)',
    preview: [91, 342, 456, 619, 788, 1042, 1205, 1567, 2034, 2891, 55, 3300],
    atScale: {
      n: 500,
      leftLabel: 'Bubble Sort',
      leftNote: '~124,750 comparisons',
      rightLabel: 'Insertion Sort',
      rightNote: '~503 comparisons',
    },
  },
}
